import { captureMessage } from '@sentry/react';
import { SplitFactory } from '@splitsoftware/splitio';
import cuid from 'cuid';
import { pickBy } from 'ramda';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { config } from 'src/config';
import type { User } from 'src/shared/graphql/generated';
import {
  isBrowser,
  isMobileApp,
  isServer,
} from 'src/shared/helpers/environment';
import { isDefined } from 'src/shared/helpers/guards';
import { useNonce } from 'src/shared/hooks';
import type { SplitContext } from 'src/shared/hooks/useSplit';
import { Provider } from 'src/shared/hooks/useSplit';

import { loadMockedFeatures } from './mockedFeatures';

const generateAnonymousId = () => `anonymous-${cuid()}`;

interface SplitProps extends PropsWithChildren<{}> {}
type UnfilteredAttributes = {
  [key: string]: number | string | boolean | undefined | null;
};

const Split = ({ children }: SplitProps) => {
  const [splitFactory, userId] = useMemo(() => {
    if (!config.SPLIT_API_KEY) return [null, ''];

    const key = isServer() ? generateAnonymousId() : window.splitUserId;

    /**
     * @see https://help.split.io/hc/en-us/articles/360020448791-JavaScript-SDK#localhost-mode
     */
    const features =
      isBrowser() && config.SPLIT_API_KEY === 'localhost'
        ? loadMockedFeatures()
        : undefined;

    const factory = SplitFactory({
      core: {
        authorizationKey: config.SPLIT_API_KEY,
        key,
      },
      features,
      startup: {
        readyTimeout: 20,
      },
    });

    return [factory, key];
  }, []);

  const [client, setClient] = useState(splitFactory?.client());
  const [attributes, setAttributes] = useState({});
  const nonce = useNonce();

  // Keep the client promise the same across re-renders
  const getClient = useCallback(async () => {
    try {
      // When using async/await syntax with the split client, the rejection
      // should be explicitly propagated or the promise will hang.
      // More details found at https://github.com/splitio/javascript-commons/blob/39af1a1/src/readiness/sdkReadinessManager.ts#L95-L107
      await client?.ready().catch(e => {
        throw e;
      });
    } catch (error) {
      captureMessage((error as Error).message, 'warning');
    }

    return client;
  }, [client]);

  const updateUserId = (id: string) => {
    if (splitFactory?.client) {
      setClient(splitFactory.client(id));
    }
  };

  const updateAttributes = (user?: User) => {
    if (user) {
      const org = user.orgs[0];
      const partner = org?.partner;

      const unfilteredAttrs: UnfilteredAttributes = {
        user_id: user?.id,
        email: user?.email,
        org_id: org?.id,
        orgId: org?.id,
        org_priority: org?.priority,
        on_waitlist: org?.onWaitlist,
        partner_id: partner?.id,
        partnerId: partner?.id,
        isHomeowner: user?.isHomeowner,
        clientApp: 'design-studio',
        app_name: 'web-react',
        appName: 'web-react',
      };

      if (isMobileApp() && window.HOVERApp?.version) {
        unfilteredAttrs.app_version = window.HOVERApp.version;
        unfilteredAttrs.device_os = window.HOVERApp.platform.name;
        unfilteredAttrs.device_os_version = window.HOVERApp.platform.version;
      }

      const valueIsDefined = (_: any, key: string | number) =>
        isDefined(unfilteredAttrs[key]);
      const attr: SplitIO.Attributes = pickBy(valueIsDefined, unfilteredAttrs);
      if (JSON.stringify(attributes) !== JSON.stringify(attr)) {
        setAttributes(attr);
      }
    }
  };

  const contextValue: SplitContext = {
    attributes,
    client: getClient(),
    updateAttributes,
    updateUserId,
  };

  return (
    <>
      {isServer() && (
        <Helmet>
          <script nonce={nonce}>{`window.splitUserId = '${userId}'`}</script>
        </Helmet>
      )}
      <Provider value={contextValue}>{children}</Provider>
    </>
  );
};

export { Split };
